import React, { useEffect, useState } from "react";
import { X, Save } from "lucide-react";

/**
 * System-style modal dialog for editing a single note field. Used for task notes
 * and block notes. Centered, dismiss on Escape or outside-click. Submit on Cmd/Ctrl+Enter.
 */
export default function NoteDialog({ open, title, initialValue, placeholder, onSave, onClose }) {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setValue(initialValue || "");
  }, [open, initialValue]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const save = async () => {
    setBusy(true);
    try {
      await onSave(value);
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="note-dialog-overlay"
    >
      <div
        className="w-[520px] max-w-[92vw] bg-bg-surface border border-line-strong shadow-[0_24px_64px_rgba(0,0,0,0.8)] animate-slide-up"
        onClick={(e) => e.stopPropagation()}
        data-testid="note-dialog"
      >
        <div className="flex items-center justify-between p-3 border-b border-line-strong bg-bg-elevated">
          <div>
            <div className="mono text-2xs uppercase tracking-[0.2em] text-ink-mute">NOTE</div>
            <div className="text-sm text-ink mt-0.5 truncate">{title}</div>
          </div>
          <button onClick={onClose} className="btn-ghost" data-testid="note-dialog-close">
            <X className="w-3 h-3" />
          </button>
        </div>

        <div className="p-4">
          <textarea
            autoFocus
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={placeholder || "add notes…"}
            className="w-full bg-bg-elevated text-xs p-3 outline-none border border-line focus:border-amber min-h-[180px] leading-relaxed"
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                e.preventDefault();
                save();
              }
            }}
            data-testid="note-dialog-input"
          />
          <p className="mono text-2xs text-ink-mute uppercase tracking-widest mt-2">
            Cmd/Ctrl + Enter to save · Escape to cancel
          </p>
        </div>

        <div className="p-3 border-t border-line bg-bg-surface flex items-center justify-end gap-2">
          <button onClick={onClose} className="btn-ghost" data-testid="note-dialog-cancel">CANCEL</button>
          <button onClick={save} disabled={busy} className="btn-primary px-4" data-testid="note-dialog-save">
            <Save className="w-3 h-3" /> {busy ? "Saving…" : "Save note"}
          </button>
        </div>
      </div>
    </div>
  );
}
